import React, { useState, useEffect } from 'react';
import {
  Calculator,
  TrendingDown,
  PiggyBank,
  FileText,
  AlertTriangle,
  CheckCircle,
  Building,
  Calendar,
  Info,
  ChevronRight,
  Shield
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { calculateGermanTaxes } from './germanTaxCalculations';

interface TaxOptimizationResult {
  currentTaxBurden: number;
  optimizedTaxBurden: number;
  savings: number;
  savingsPercent: number;
  strategies: TaxStrategy[];
  afaDetails: AfADetails;
  speculationPeriod: SpeculationPeriodInfo;
  threeObjectRule: ThreeObjectRuleStatus;
}

interface TaxStrategy {
  id: string;
  title: string;
  description: string;
  potentialSaving: number;
  difficulty: 'easy' | 'medium' | 'hard';
  legalParagraph: string;
  implemented: boolean;
  requirements: string[];
}

interface AfADetails {
  regularAfA: number; // §7 Abs. 4 EStG - 2% or 2.5%
  denkmalAfA: number; // §7i EStG - 9% for 12 years
  energyAfA: number; // §7h EStG - 5% for renovations
  sonderAfA: number; // Special depreciation
  totalAfA: number;
  buildingValue: number;
  landValue: number;
  afaRate: number;
}

interface SpeculationPeriodInfo {
  purchaseDate: Date;
  speculationEndDate: Date;
  daysRemaining: number;
  taxFreeAfter: boolean;
  estimatedCapitalGains: number;
  capitalGainsTax: number;
}

interface ThreeObjectRuleStatus {
  salesInLast5Years: number;
  remainingBeforeCommercial: number;
  isCommercialTrading: boolean;
  taxImpact: string;
}

const TaxOptimizer: React.FC = () => {
  const { portfolio, currentInputs } = useApp();
  const [selectedProperty, setSelectedProperty] = useState<number>(-1);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<TaxOptimizationResult | null>(null);
  
  // Tax settings
  const [taxSettings, setTaxSettings] = useState({
    marginalTaxRate: 42, // Spitzensteuersatz
    churchTax: true, // Kirchensteuer 8-9%
    solidarityTax: true, // Solidaritätszuschlag 5.5%
    married: false, // Ehegattensplitting
    isDenkmal: false, // Listed building
    hasEnergyRenovation: false,
    useSonderAfA: false,
    landValuePercentage: 20, // Bodenwertanteil
  });

  useEffect(() => {
    calculateOptimization();
  }, [selectedProperty, taxSettings, portfolio, currentInputs]);

  const calculateOptimization = () => {
    let propertyData = null;
    
    if (selectedProperty >= 0 && portfolio[selectedProperty]) {
      propertyData = portfolio[selectedProperty].inputs;
    } else if (currentInputs) {
      propertyData = currentInputs;
    }
    
    if (!propertyData) return;

    const result = calculateGermanTaxes(propertyData, taxSettings, taxYear);
    setOptimizationResult(result);
  };

  const getPropertyForAnalysis = () => {
    if (selectedProperty >= 0 && portfolio[selectedProperty]) {
      return portfolio[selectedProperty];
    }
    return currentInputs ? { inputs: currentInputs, calculations: null } : null;
  };

  const property = getPropertyForAnalysis();

  const renderAfAOptimization = () => {
    if (!optimizationResult) return null;
    const { afaDetails } = optimizationResult;

    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Building className="w-5 h-5 mr-2 text-indigo-600" />
          Abschreibung (AfA) Optimierung
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Gebäudewert</div>
            <div className="text-xl font-bold text-gray-900">
              {formatCurrency(afaDetails.buildingValue)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {(100 - taxSettings.landValuePercentage)}% des Kaufpreises
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Grundstückswert</div>
            <div className="text-xl font-bold text-gray-900">
              {formatCurrency(afaDetails.landValue)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {taxSettings.landValuePercentage}% des Kaufpreises
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Regular AfA */}
          <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Lineare AfA (§7 Abs. 4 EStG)</div>
                <div className="text-sm text-gray-600">
                  {afaDetails.afaRate}% jährlich ({(property?.inputs.baujahr || 1990) < 1925 ? '40' : '50'} Jahre)
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-bold text-gray-900">{formatCurrency(afaDetails.regularAfA)}</div>
              <div className="text-xs text-gray-500">pro Jahr</div>
            </div>
          </div>

          {/* Denkmal AfA */}
          {taxSettings.isDenkmal && (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Denkmal-AfA (§7i EStG)</div>
                  <div className="text-sm text-gray-600">9% für 12 Jahre auf Sanierungskosten</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-green-600">{formatCurrency(afaDetails.denkmalAfA)}</div>
                <div className="text-xs text-gray-500">pro Jahr</div>
              </div>
            </div>
          )}

          {/* Energy AfA */}
          {taxSettings.hasEnergyRenovation && (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                  <TrendingDown className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Energetische Sanierung (§35c EStG)</div>
                  <div className="text-sm text-gray-600">20% über 3 Jahre verteilt</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-emerald-600">{formatCurrency(afaDetails.energyAfA)}</div>
                <div className="text-xs text-gray-500">Steuerersparnis</div>
              </div>
            </div>
          )}

          {/* Total AfA */}
          <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-indigo-600 font-medium">Gesamte jährliche AfA</div>
                <div className="text-xs text-indigo-500 mt-1">Steuerliche Abschreibung</div>
              </div>
              <div className="text-2xl font-bold text-indigo-700">
                {formatCurrency(afaDetails.totalAfA)}
              </div>
            </div>
            <div className="mt-2 text-sm text-indigo-600">
              Steuerersparnis: {formatCurrency(afaDetails.totalAfA * (taxSettings.marginalTaxRate / 100))}
            </div>
          </div>
        </div>

        {/* Land/Building Split Optimizer */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gebäude-/Grundstücksaufteilung optimieren
          </label>
          <input
            type="range"
            min="10"
            max="40"
            value={taxSettings.landValuePercentage}
            onChange={(e) => setTaxSettings({...taxSettings, landValuePercentage: Number(e.target.value)})}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>10% Grund</span>
            <span className="font-medium">{taxSettings.landValuePercentage}% Grundstück</span>
            <span>40% Grund</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSpeculationPeriod = () => {
    if (!optimizationResult || !property) return null;
    const { speculationPeriod } = optimizationResult;

    const isWithinSpeculation = speculationPeriod.daysRemaining > 0;
    const daysRemaining = Math.max(0, speculationPeriod.daysRemaining);
    const yearsRemaining = Math.floor(daysRemaining / 365);
    const monthsRemaining = Math.floor((daysRemaining % 365) / 30);

    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
          Spekulationsfrist (§23 EStG)
        </h3>

        {isWithinSpeculation ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-yellow-900">Innerhalb der Spekulationsfrist</h4>
                <p className="text-sm text-yellow-800 mt-1">
                  Verkauf würde zu steuerpflichtigem Gewinn führen
                </p>
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-yellow-700">Verbleibende Zeit</div>
                    <div className="text-lg font-bold text-yellow-900">
                      {yearsRemaining} Jahre, {monthsRemaining} Monate
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-yellow-700">Geschätzte Steuer bei Verkauf</div>
                    <div className="text-lg font-bold text-red-600">
                      {formatCurrency(speculationPeriod.capitalGainsTax)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900">Außerhalb der Spekulationsfrist</h4>
                <p className="text-sm text-green-800 mt-1">
                  Verkauf wäre steuerfrei (privates Veräußerungsgeschäft)
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Details zur Spekulationsfrist</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Kaufdatum</span>
              <span className="font-medium">{speculationPeriod.purchaseDate.toLocaleDateString('de-DE')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Ende der Spekulationsfrist</span>
              <span className="font-medium">{speculationPeriod.speculationEndDate.toLocaleDateString('de-DE')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Geschätzter Verkaufsgewinn</span>
              <span className="font-medium">{formatCurrency(speculationPeriod.estimatedCapitalGains)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderThreeObjectRule = () => {
    if (!optimizationResult) return null;
    const { threeObjectRule } = optimizationResult;

    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2 text-indigo-600" />
          Drei-Objekt-Grenze (Gewerblicher Handel)
        </h3>

        {threeObjectRule.isCommercialTrading ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-900">Gewerblicher Grundstückshandel</h4>
                <p className="text-sm text-red-800 mt-1">
                  Sie haben die Drei-Objekt-Grenze überschritten. Alle Gewinne sind gewerblich steuerpflichtig.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900">Private Vermögensverwaltung</h4>
                <p className="text-sm text-green-800 mt-1">
                  Sie können noch {threeObjectRule.remainingBeforeCommercial} Objekt(e) verkaufen, ohne als gewerblich zu gelten.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold text-gray-900">{threeObjectRule.salesInLast5Years}</div>
            <div className="text-xs text-gray-600">Verkäufe in 5 Jahren</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold text-indigo-600">{threeObjectRule.remainingBeforeCommercial}</div>
            <div className="text-xs text-gray-600">Verbleibende Verkäufe</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-lg font-bold text-gray-900">{threeObjectRule.taxImpact}</div>
            <div className="text-xs text-gray-600">Steuerlicher Status</div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaxStrategies = () => {
    if (!optimizationResult) return null;
    const { strategies, currentTaxBurden, optimizedTaxBurden, savings } = optimizationResult;

    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PiggyBank className="w-5 h-5 mr-2 text-indigo-600" />
          Steueroptimierungsstrategien
        </h3>

        {/* Savings Summary */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-4 border border-green-200">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">Aktuelle Steuerlast</div>
              <div className="text-xl font-bold text-gray-900">{formatCurrency(currentTaxBurden)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Optimierte Steuerlast</div>
              <div className="text-xl font-bold text-green-600">{formatCurrency(optimizedTaxBurden)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Potenzielle Ersparnis</div>
              <div className="text-xl font-bold text-green-700">
                {formatCurrency(savings)}
                <span className="text-sm font-normal ml-1">({formatPercent(savings / currentTaxBurden)})</span>
              </div>
            </div>
          </div>
        </div>

        {/* Strategy List */}
        <div className="space-y-3">
          {strategies.map((strategy) => (
            <div
              key={strategy.id}
              className={`p-4 rounded-lg border ${
                strategy.implemented 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-white border-gray-200 hover:border-indigo-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-medium text-gray-900">{strategy.title}</h4>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      strategy.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      strategy.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {strategy.difficulty === 'easy' ? 'Einfach' :
                       strategy.difficulty === 'medium' ? 'Mittel' : 'Komplex'}
                    </span>
                    {strategy.implemented && (
                      <CheckCircle className="w-4 h-4 text-green-600 ml-2" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{strategy.description}</p>
                  <div className="mt-2 flex items-center text-xs text-gray-500">
                    <FileText className="w-3 h-3 mr-1" />
                    {strategy.legalParagraph}
                  </div>
                  {strategy.requirements.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-700">Voraussetzungen:</div>
                      <ul className="mt-1 text-xs text-gray-600">
                        {strategy.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start">
                            <ChevronRight className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(strategy.potentialSaving)}
                  </div>
                  <div className="text-xs text-gray-500">Ersparnis/Jahr</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Calculator className="w-8 h-8 mr-3" />
              Steueroptimierung für deutsche Immobilien
            </h1>
            <p className="text-blue-100 mt-1">
              Maximieren Sie Ihre AfA, minimieren Sie Ihre Steuerlast - 100% legal nach deutschem Steuerrecht
            </p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{taxYear}</div>
            <select
              value={taxYear}
              onChange={(e) => setTaxYear(Number(e.target.value))}
              className="mt-1 px-2 py-1 text-sm bg-white/20 rounded border border-white/30 text-white"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year} className="text-gray-900">{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Property Selector */}
      {portfolio.length > 0 && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Immobilie für Analyse auswählen
          </label>
          <select
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={-1}>Aktuelle Eingabe verwenden</option>
            {portfolio.map((prop, idx) => (
              <option key={idx} value={idx}>
                {prop.inputs.propertyName || `Immobilie ${idx + 1}`} - {formatCurrency(prop.inputs.angebotspreis)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tax Settings */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Steuerliche Einstellungen</h3>
          <button
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="text-sm text-indigo-600 hover:text-indigo-700"
          >
            {showAdvancedSettings ? 'Weniger' : 'Erweiterte'} Einstellungen
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grenzsteuersatz (%)
            </label>
            <input
              type="number"
              value={taxSettings.marginalTaxRate}
              onChange={(e) => setTaxSettings({...taxSettings, marginalTaxRate: Number(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              min="0"
              max="45"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={taxSettings.married}
                onChange={(e) => setTaxSettings({...taxSettings, married: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Ehegattensplitting</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={taxSettings.churchTax}
                onChange={(e) => setTaxSettings({...taxSettings, churchTax: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Kirchensteuer</span>
            </label>
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={taxSettings.isDenkmal}
                onChange={(e) => setTaxSettings({...taxSettings, isDenkmal: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Denkmalschutz</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={taxSettings.hasEnergyRenovation}
                onChange={(e) => setTaxSettings({...taxSettings, hasEnergyRenovation: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Energetische Sanierung</span>
            </label>
          </div>
        </div>

        {showAdvancedSettings && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={taxSettings.useSonderAfA}
                  onChange={(e) => setTaxSettings({...taxSettings, useSonderAfA: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Sonder-AfA für Mietwohnungsneubau (§7b EStG)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={taxSettings.solidarityTax}
                  onChange={(e) => setTaxSettings({...taxSettings, solidarityTax: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Solidaritätszuschlag (5,5%)</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderAfAOptimization()}
        {renderSpeculationPeriod()}
        {renderThreeObjectRule()}
        {renderTaxStrategies()}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900">Rechtlicher Hinweis</h4>
            <p className="text-sm text-blue-800 mt-1">
              Diese Berechnungen dienen nur zur Orientierung und ersetzen keine professionelle Steuerberatung. 
              Konsultieren Sie einen Steuerberater für verbindliche Auskünfte zu Ihrer individuellen Situation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxOptimizer;